import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import {
  IDigitalDeliveryResponse,
  IUpdateDigitalDeliveryStatus,
} from 'services/digital-delivery/types';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  IDigitalDeliveryInformation,
  IMusicProductDigitalDeliveryServiceResponse,
} from './types/query';

export const useGetMusicProductDigitalDeliveries = (
  DigitalMusicProductId: string,
  OnlyDefaultProviders = true
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IMusicProductDigitalDeliveryServiceResponse[], DefaultError>({
    queryKey: [
      'music-product-digital-delivery',
      DigitalMusicProductId,
      OnlyDefaultProviders,
    ],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `MusicProductDigital/Deliveries`,
        params: { DigitalMusicProductId, OnlyDefaultProviders },
      });

      return responseHandler(response, {
        groupName: `useGetMusicProductDigitalDeliveries - ${DigitalMusicProductId}`,
        notFoundPath: '/music-product-digital',
      });
    },
  });
};

export const useGetMusicProductDigitalDeliveryInformation = (
  DigitalMusicProductId: string
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IDigitalDeliveryInformation, DefaultError>({
    queryKey: [
      'music-product-digital-delivery-Information',
      DigitalMusicProductId,
    ],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `MusicProductDigital/DeliveryInformation`,
        params: { DigitalMusicProductId },
      });

      return responseHandler(response, {
        groupName: `useGetMusicProductDigitalDeliveryInformation - ${DigitalMusicProductId}`,
        onNotFound: () => undefined,
      });
    },
    retry: false,
  });
};

export const useGetMusicProductDigitalDeliveryConsistent = (
  DigitalMusicProductId: string
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IDigitalDeliveryResponse | null, DefaultError>({
    queryKey: [
      'music-product-digital-delivery-consistent',
      DigitalMusicProductId,
    ],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `MusicProductDigital/Deliveries/Consistent`,
        params: { DigitalMusicProductId },
      });

      return responseHandler(response, {
        groupName: `useGetMusicProductDigitalDeliveryConsistent - ${DigitalMusicProductId}`,
        onNotFound: () => undefined,
      });
    },
    retry: false,
  });
};

export const useChangeDigitalDeliveryStatus = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useChangeDigitalDeliveryStatus'
  );
  return useMutation<{}, DefaultError, IUpdateDigitalDeliveryStatus>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('digital-delivery.what'),
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'MusicProductDigital/Deliveries/ChangeStatus',
        body,
      });

      return responseHandler(response, {
        groupName: 'useChangeDigitalDeliveryStatus',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('digital-delivery.what'),
        })
      );
    },
    onError: dismiss,
  });
};
